import { Effect, ModifierEffect, Target, TargetType, TriggerFrame, Value } from "./effectTypes";

const CONTROLLERS = {
    self: ["you", "your", "you control"],
    opponent: ["an opponent", "opponent's", "an opponent controls"],
    target: ["target", "target's", "target player controls"],
} as const;

function pluralize(base: string, count: number | "all" | undefined): string {
    if (count === "all" || (typeof count === "number" && count !== 1)) {
        return base + "s";
    }
    return base;
}

function describeTarget(target?: TargetType): string {
    if (!target) return "[Invalid target definition]";

    if (target.type === "stored-card") {
        return "that spell";
    }

    const count = target.count;
    const countWord =
        count === "all" ? "all " : typeof count === "number" ? `${count} ` : "";

    const ctrl = target.controller ? CONTROLLERS[target.controller] : null;

    switch (target.type) {
        case "player": {
            const prefix = !countWord && !ctrl ? "target " : countWord === "all " ? "" : "";
            const noun = ctrl ? ctrl[0] : "player";
            return `${prefix}${countWord}${noun}${countWord ? "s" : ""}`;
        }

        case "class": {
            const prefix = ctrl ? ctrl[1] : "target player's";
            return `${countWord}${prefix} class${countWord ? "s" : ""}`;
        }

        case "card": {
            return describeCardTarget(target, countWord, ctrl);
        }

        default:
            return "[Unknown target type]";
    }
}

function describeCardTarget(
    target: Target,
    countWord: string,
    ctrl: readonly string[] | null
): string {
    const { id, idNot, zone } = target;

    const isThis = id === "this";
    const isThat = id && id !== "this";
    const excludeThis = idNot === "this";

    const determiner = isThis ? "this" : isThat ? "that" : null;
    const otherPrefix = excludeThis ? (countWord ? "other " : "another ") : "";

    const zonePhrase =
        zone === "hand"
            ? " in hand"
            : zone === "discard"
                ? " in discard"
                : zone === "battlefield"
                    ? ""
                    : "";

    const controllerPhrase = ctrl ? ` ${ctrl[2]}` : "";

    // No zone/determiner: "target spell", "2 other spells", "all spells you control"
    if (!determiner && !zone) {
        const prefix = countWord ? "" : "target ";
        return `${countWord}${otherPrefix}${prefix}spell${pluralize(
            "",
            target.count
        )}${controllerPhrase}`;
    }

    // Determiner only: "this card", "that other card", "those 2 cards"
    if (determiner && !zone) {
        const plural = typeof target.count === "number" && target.count !== 1;
        const det = isThis ? "this" : plural ? "those" : "that";
        return `${countWord}${det} ${otherPrefix}card${pluralize(
            "",
            target.count
        )}${controllerPhrase}`;
    }

    // Zone only: "2 target spells in hand", "all other spells in discard"
    if (!determiner && zone) {
        const prefix = countWord || otherPrefix ? "" : "target ";
        return `${countWord}${otherPrefix}${prefix}spell${pluralize(
            "",
            target.count
        )}${zonePhrase}${controllerPhrase}`;
    }

    // Both determiner and zone: "this spell in hand", "those 2 other spells in discard"
    const plural = typeof target.count === "number" && target.count !== 1;
    const det = isThis ? "this" : plural ? "those" : "that";
    return `${countWord}${det} ${otherPrefix}spell${pluralize(
        "",
        target.count
    )}${zonePhrase}${controllerPhrase}`;
}

function joinEffects(effects: Effect[]): string {
    if (effects.length === 0) return "";
    if (effects.length === 1) return createText(effects[0]);

    const texts = effects.map(createText);
    const last = texts.pop()!;
    return `${texts.join(", ")}, then ${last}`;
}

function createAmountText(amount?: number | Value, top: boolean = true, replaceOneWithA: boolean = true): string {
    if (typeof amount === "number") {
        return (amount === 1 && replaceOneWithA ? "a" : `${amount}`);
    }

    if (amount === undefined || amount === null) {
        return ' [invalid amount]';
    }

    switch (amount.type) {
        case "property":
            switch (amount.property) {
                case "cost":
                    return `this card's cost`;
                case "castThreshold":
                    return `this card's cast threshold`;
                case "focusTotal":
                    return `the total focus on this card`;
                case "focus":
                    return `the non-temp focus on this card`;
                case "focusTemp":
                    return `the temporary focus on this card`;
                case "universalId":
                    return `the card`
            }
            break;
        case "user":
            return `X`;
        case "modifier-value":
            return `that amount${top ? ` of` : ''}`;
        case "ritual-value":
            return `[ritual intensity]`;
        case "math":
            const paren = !top;
            if (typeof amount.left === "number" && typeof amount.right === "number") {
                switch (amount.sign) {
                    case "*":
                        return `${amount.left * amount.right}`;
                    case "/":
                        return `${amount.left / amount.right}`;
                    case "+":
                        return `${amount.left + amount.right}`;
                    case "-":
                        return `${amount.left - amount.right}`;
                    case "^":
                        return `${amount.left ^ amount.right}`;
                }
            }
            return `${paren ? `` : `[`}${createAmountText(amount.left, top, false)} ${amount.sign} ${createAmountText(amount.right, top, false)}${paren ? `` : `]`}`;
        case "stored-value":
            if (amount.key === "amount") {
                return "that amount of ";
            }
            return `${amount.key}`;
        case "target-count":
            const tgt = amount.target;
            if (!tgt)
                return "[invalid target-count]";

            if (tgt.type === "stored-card") {
                // this is a really weird case, but just in case?
                return "1"
            }

            const ctrl = tgt.controller ? CONTROLLERS[tgt.controller] : null;
            const countWord = tgt.count === "all" ? "all " : typeof tgt.count === "number" ? `${tgt.count} ` : "";

            switch (tgt.type) {
                case "player":
                    const noun = ctrl ? ctrl[0] : "target player";
                    return `(the number of ${noun}${countWord ? "s" : ""})`;
                case "card":
                    const otherPrefix = tgt.idNot === "this" ? (countWord ? "other " : "another ") : "";
                    const zonePhrase = tgt.zone === "hand" ? " in hand" : tgt.zone === "discard" ? " in discard" : "";
                    const controllerPhrase = ctrl ? ` ${ctrl[2]}` : "";
                    return `(the number of ${otherPrefix}spells${zonePhrase}${controllerPhrase})`;
                case "class":
                    const prefix = ctrl ? ctrl[1] : "target player's";
                    return `(the number of ${prefix} class${pluralize("", tgt.count)})`;
                default:
                    return "[unknown target-count type]";
            }
    }
}

function createModifierText(
    triggerConditional: TriggerFrame,
    modifier: ModifierEffect,
    additionalEffect: boolean
): string {
    let plural =
        triggerConditional.on.type !== "stored-card" &&
        triggerConditional.on.controller !== "self";
    if (triggerConditional.on.type === "stored-card") {
    }

    switch (modifier.update) {
        case "void":
            return additionalEffect ? " instead" : " prevent it";
        case "amount":
            if (!modifier.effect)
                return `instead`;

            let preface = "";
            switch (triggerConditional.condition) {
                case "damage":
                    preface = plural ? "they take" : "take";
                    break;
                case "heal":
                    preface = plural ? "they heal" : "heal";
                    break;
                case "draw":
                    preface = plural ? "they draw" : "draw";
                    break;
                case "discard":
                    preface = plural ? "they discard" : "discard";
                    break;
                case "remove-focus":
                    preface = plural ? "they remove" : "remove";
                    break;
                case "add-focus":
                    preface = plural ? "they add" : "add";
                    break;
                case "targets":
                    preface = plural ? "they do" : "do";
                    break;
                case "ritual":
                    preface = plural ? "they get" : "get";
                    break;
                case "ub-damage":
                case "create":
                case "tutor":
                case "any":
                case "gain-control":
                case "counter":
                    preface = " [unimplemented] ";
            }
            return ` ${preface} ${createAmountText(
                modifier.effect,
                false,
                false
            )} instead`;
    }
}

function createTriggerConditionalText(triggerConditional: TriggerFrame): string {
    let altSubject = null;
    let abilitySrc = null;
    let plural =
        triggerConditional.on.type !== "stored-card" &&
        triggerConditional.on.controller !== "self";
    if (triggerConditional.on.type === "stored-card") {
        altSubject = 'that card';
    }

    switch (triggerConditional.source) {
        case "cast":
            abilitySrc = "cast";
            break;
        case "play":
            abilitySrc = "play";
            break;
        case "sustain":
            abilitySrc = "sustain";
            break;
        case "passive":
            abilitySrc = "passive";
            break;
        case "any":
            break;
        case "SYSTEM":
            abilitySrc = "system";
            break;
    }

    switch (triggerConditional.condition) {
        case "damage":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } take${plural ? "s" : ""} damage${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "heal":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } heal${plural ? "s" : ""}${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "draw":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } draw${plural ? "s" : ""}${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "discard":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } discard${plural ? "s" : ""}${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "gain-control":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } is stolen${abilitySrc ? ` by a ${abilitySrc}` : ``}`;
        case "counter":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } is countered${abilitySrc ? ` by a ${abilitySrc}` : ``}`;
        case "add-focus":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } has focus added${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "remove-focus":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } has focus removed${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "targets":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } is targeted${abilitySrc ? ` by a ${abilitySrc}` : ``}`;
        case "ub-damage":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } take${plural ? "s" : ""
                } void damage${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "tutor":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } search${plural ? "es" : ""
                } the deck${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "ritual":
            return `${altSubject ??
                describeTarget(triggerConditional.on).replace("target", "a")
                } gain${plural ? "s" : ""
                } ritual${abilitySrc ? ` from a ${abilitySrc}` : ``}`;
        case "create":
            return '[unimplemeted]';
        case "any":
            return `a${abilitySrc ? " " + abilitySrc : "n"} ability triggers ${altSubject ??
                "on " + describeTarget(triggerConditional.on).replace("target", "a")
                }`;

    }
}

export function createText(effect: Effect | null): string {
    if (effect === null || effect === undefined) return "";

    if (effect.type === "multiple") {
        return joinEffects(effect.set);
    }

    if (effect.type === "iterated") {
        const inner = createText(effect.effect);
        return effect.count === 1 ? inner : `${inner} ${createAmountText(effect.count)} times`;
    }

    if (effect.type === "trigger") {
        const triggerConditional = createTriggerConditionalText(effect);
        let inner = "";
        let replacement = "";

        if (effect.then) {
            replacement = createModifierText(
                effect,
                effect.then,
                effect.and !== undefined
            );
        }

        if (effect.and)
            inner = ` ${createText(effect.and)}`;

        let result = `When ${triggerConditional}`;
        if (inner && replacement) {
            // Both present: "When X, Y. It takes N instead." or "When X, Y, then Z."
            // Using: "When X, Y, and it takes N instead."
            if (replacement === " instead") {
                result += `, ${inner}${replacement}`;
            } else {
                result += `, ${inner}, and${replacement}`;
            }
        } else if (replacement) {
            result += `, ${replacement}`;
        } else if (inner) {
            result += `, ${inner}`;
        }
        return result;
    }

    const tgt = describeTarget(effect.target);
    let suffix = "";
    let subject = "";

    switch (effect.type) {
        case "ub-damage":
            return `deal ${createAmountText(effect.amount)} void damage to ${tgt}. This damage can't be modified.`;
        case "damage":
            return `deal ${createAmountText(effect.amount)} damage to ${tgt}`;
        case "draw":
            subject = tgt === "you" ? "draw" : `${tgt} draws`;
            return `${subject} ${createAmountText(effect.amount)} card${effect.amount !== 1 ? "s" : ""}`;
        case "discard":
            subject = tgt === "you" ? "discard" : `${tgt} discards`;
            return `${subject} ${createAmountText(effect.amount)} card`;
        case "counter":
            return `counter ${tgt}`;
        case "remove-focus":
            suffix = effect.target?.type === "player" ? `${tgt}'s pool` : tgt;
            return `remove ${createAmountText(effect.amount)} focus from ${suffix}`;
        case "add-focus":
            suffix = effect.target?.type === "player" ? `${tgt}${effect.target.controller === "self" ? `r` : `'s`} pool` : tgt;
            return `add ${createAmountText(effect.amount)} focus to ${suffix}`;
        case "heal":
            return `${tgt} gain${tgt === "you" ? "" : "s"} ${createAmountText(effect.amount)} life`;
        case "store":
            return `note ${tgt}`;
        case "gain-control":
            return `gain control of ${tgt}`;
        case "give-control":
            const tgt2 = describeTarget(effect.secondaryTarget);
            return `give control of ${tgt} to ${tgt2}`;
        case "tutor":
            if (effect.amount && effect.value) {
                subject = tgt === "you" ? "search your" : `${tgt} searches their`;
                return `${subject} deck for [${effect.value}]`;
            } else {
                subject = tgt === "you" ? "search your" : `${tgt} searches their`;
                return `${subject} deck for a card`;
            }
        case "create":
            subject = tgt === "you" ? "create a copy" : `${tgt} creates a copy`;
            return `${subject} of [${effect.value}]`;
        case "ritual":
            return `Ritual Intensity = ${createAmountText(effect.amount, false)}`;
    }
}